import { useState } from 'react';
import { useOpenCreditExemption } from '../api/credit.ts';
import { useT } from '../i18n/index.ts';
import { Button, Dialog, FormField, Input, Textarea } from '../ui/index.ts';
import { coerceErrorMessage } from './Toast.tsx';

interface CreditExemptionDialogProps {
  readonly orgId: string;
  readonly customerEmail: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function CreditExemptionDialog({
  orgId,
  customerEmail,
  open,
  onOpenChange,
}: CreditExemptionDialogProps): JSX.Element {
  const t = useT();
  const openExemption = useOpenCreditExemption();
  const [expiresLocal, setExpiresLocal] = useState('');
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const busy = openExemption.isPending;

  function reset(): void {
    setExpiresLocal('');
    setReason('');
    setValidation(null);
    openExemption.reset();
  }

  function handleSubmit(): void {
    if (expiresLocal.trim().length === 0) {
      setValidation(t('credit.exemption.error.expires'));
      return;
    }
    if (reason.trim().length === 0) {
      setValidation(t('credit.exemption.error.reason'));
      return;
    }
    const expiresAt = new Date(expiresLocal).toISOString();
    if (Number.isNaN(Date.parse(expiresAt))) {
      setValidation(t('credit.exemption.error.expires'));
      return;
    }
    setValidation(null);
    openExemption.mutate(
      { orgId, expiresAt, reason: reason.trim() },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  }

  const serverError =
    openExemption.error !== null ? coerceErrorMessage(openExemption.error) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) {
          if (!next) reset();
          onOpenChange(next);
        }
      }}
      title={t('credit.exemption.title')}
      description={t('credit.exemption.description')}
      closeLabel={t('common.close')}
      testId="credit-exemption-dialog"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="credit-exemption-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={busy}
            data-testid="credit-exemption-confirm"
          >
            {t('credit.exemption.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 rounded-lg bg-well px-3 py-2.5">
          <span className="text-label text-paper-low">{t('credit.apply.customer')}</span>
          <span className="text-body font-medium text-paper">{customerEmail}</span>
          <span className="font-mono text-micro text-paper-low">{orgId}</span>
        </div>
        <FormField
          label={t('credit.exemption.expires_label')}
          fieldId="credit-exemption-expires"
          required
          hint={t('credit.exemption.expires_hint')}
        >
          <Input
            id="credit-exemption-expires"
            type="datetime-local"
            value={expiresLocal}
            onChange={(event) => setExpiresLocal(event.currentTarget.value)}
            data-testid="credit-exemption-expires"
          />
        </FormField>
        <FormField
          label={t('credit.exemption.reason_label')}
          fieldId="credit-exemption-reason"
          required
          hint={t('credit.exemption.reason_hint')}
        >
          <Textarea
            id="credit-exemption-reason"
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            data-testid="credit-exemption-reason"
          />
        </FormField>
        {validation !== null ? (
          <p className="text-dense text-danger" data-testid="credit-exemption-validation">
            {validation}
          </p>
        ) : null}
        {serverError !== null ? (
          <p className="text-dense text-danger" data-testid="credit-exemption-error">
            {serverError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
